// Phase 36 Batch O — Stage breakdown card.
//
// Top-of-Portfolio-Overview module. Renders 5 tiles, one per "totalable"
// bucket (Pipeline · Negotiating · Under Contract · Owned · Sold). The
// 6th bucket — Passed — is shown as a thin link-style row beneath the
// tiles so the user can still drill into walked-away deals without it
// padding the grand totals.
//
// Each tile is clickable → calls `onPick(bucket)` so the parent can
// push that bucket into the StageFilterDropdown / URL. Selected bucket
// gets an indigo ring so the visual selection state matches.
//
// All metrics are role-aware (the backend pipes /portfolio/stage-
// breakdown through compute_role_aware_metrics) — so the same tile
// reads buyer-with-counter as the counter price and seller as the
// asking price without any extra frontend logic.

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useFmt } from "@/lib/useFmt";
import {
  Building2,
  HandCoins,
  FileSignature,
  Home,
  Trophy,
  CircleSlash,
} from "lucide-react";

const ICONS = {
  pipeline: Building2,
  negotiating: HandCoins,
  under_contract: FileSignature,
  owned: Home,
  sold: Trophy,
  passed: CircleSlash,
};

// Tile accent color per bucket. Keep these tonally aligned with
// PIPELINE_STAGE_COLORS on the backend so the bucket → stage drill-down
// feels visually continuous.
const ACCENTS = {
  pipeline:       "text-slate-700 bg-slate-50 ring-slate-200",
  negotiating:    "text-amber-700 bg-amber-50 ring-amber-200",
  under_contract: "text-violet-700 bg-violet-50 ring-violet-200",
  owned:          "text-emerald-700 bg-emerald-50 ring-emerald-200",
  sold:           "text-indigo-700 bg-indigo-50 ring-indigo-200",
  passed:         "text-slate-500 bg-slate-50 ring-slate-200",
};

export default function StageBreakdownCard({ activeBucket, onPick }) {
  const fmt = useFmt();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/portfolio/stage-breakdown");
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div
        className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        data-testid="stage-breakdown-card-loading"
      >
        <div className="h-3 w-24 animate-pulse rounded bg-slate-100" />
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-xl bg-slate-50" />
          ))}
        </div>
      </div>
    );
  }

  if (!data || !Array.isArray(data.buckets)) return null;

  const totalable = data.buckets.filter((b) => !b.excluded_from_totals);
  const passed    = data.buckets.find((b) => b.bucket === "passed");
  const grand     = data.grand_totals || {};

  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
      data-testid="stage-breakdown-card"
    >
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Portfolio by stage
          </p>
          <h3 className="mt-1 text-base font-semibold text-slate-900">
            {grand.count} {grand.count === 1 ? "deal" : "deals"} across {totalable.length} stages
          </h3>
        </div>
        <div className="text-right">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Total value</p>
          <p className="mt-0.5 text-lg font-bold tabular-nums text-slate-900">
            {fmt.money(grand.total_value || 0)}
          </p>
        </div>
      </div>

      <div
        className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5"
        data-testid="stage-breakdown-tiles"
      >
        {totalable.map((b) => {
          const Icon = ICONS[b.bucket] || Building2;
          const isActive = activeBucket === b.bucket;
          const accent = ACCENTS[b.bucket] || ACCENTS.pipeline;
          return (
            <button
              key={b.bucket}
              type="button"
              data-testid={`stage-tile-${b.bucket}`}
              data-active={isActive ? "true" : "false"}
              onClick={() => onPick && onPick(b.bucket)}
              className={`group relative flex flex-col items-start gap-1 rounded-xl border bg-white p-4 text-left shadow-sm ring-1 ring-transparent transition-all hover:-translate-y-0.5 hover:shadow-md ${
                isActive
                  ? "border-indigo-300 ring-2 ring-indigo-300"
                  : "border-slate-200 hover:border-slate-300"
              }`}
            >
              <div className={`inline-flex h-7 w-7 items-center justify-center rounded-lg ${accent}`}>
                <Icon className="h-4 w-4" />
              </div>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {b.label}
              </span>
              <span
                className="text-xl font-bold tabular-nums text-slate-900"
                data-testid={`stage-tile-${b.bucket}-count`}
              >
                {b.count}
              </span>
              <span className="text-[11px] tabular-nums text-slate-500">
                {fmt.money(b.total_value || 0)}
              </span>
              {/* Phase 36 Batch P — avg payback line under the tile.
                  Renders only when the bucket has at least one
                  positive-CF deal. */}
              {b.avg_payback_period_years != null ? (
                <span
                  data-testid={`stage-tile-${b.bucket}-payback`}
                  className="mt-1 text-[10px] font-medium tabular-nums text-indigo-600"
                >
                  ~{Number(b.avg_payback_period_years).toFixed(1)} yr payback
                </span>
              ) : (
                <span className="mt-1 text-[10px] text-slate-400">—</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Passed bucket — shown as a quiet text row so it never pads the
          grand totals but the user can still drill into it. */}
      {passed && passed.count > 0 ? (
        <button
          type="button"
          data-testid="stage-tile-passed"
          onClick={() => onPick && onPick("passed")}
          className="mt-4 inline-flex items-center gap-2 text-xs text-slate-500 transition-colors hover:text-slate-800"
        >
          <CircleSlash className="h-3.5 w-3.5" />
          <span>
            <strong className="font-semibold tabular-nums text-slate-700">{passed.count}</strong>{" "}
            passed (browse only — excluded from totals)
          </span>
        </button>
      ) : null}
    </div>
  );
}
